import React, {FC} from "react";
import {
    CheckCircleOutlined,
    DeleteOutlined,
    DownloadOutlined,
    FileDoneOutlined,
    MailOutlined
} from "@ant-design/icons";
import {Alert, Button, Empty, Skeleton} from "antd";
import {useTranslation} from "react-i18next";
import {DeletePopconfirm} from "../../../Components/DeleteConfirmation";
import {PageHeader} from "../../../Components/ui";
import {shellBreadcrumbKeys, useShellBreadcrumbLabel} from "../../../Contexts/ShellBreadcrumbContext";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../../userFacingErrorUtils";
import {ActivitiesSection} from "./ActivitiesSection";
import {OrganizerRegistrationModal} from "../OrganizerRegistrationModal";
import {CertificatesSection} from "./CertificatesSection";
import {OffersPaymentsSection} from "./OffersPaymentsSection";
import type {ParticipantDetailTarget} from "./participantDetailTypes";
import {useEmptyText} from "./participantDetailUi";
import {PayerSection} from "./PayerSection";
import {QrInvitationSection} from "./QrInvitationSection";
import {RegistrationSection} from "./RegistrationSection";
import {RoomingSection} from "./RoomingSection";
import {SystemDataSection} from "./SystemDataSection";
import {useParticipantDetail} from "./useParticipantDetail";

type ParticipantDetailProps = {
    target: ParticipantDetailTarget;
    // Called after the participant is deleted, so the page can leave the removed record.
    onDeleted?: () => void;
};

export const ParticipantDetail: FC<ParticipantDetailProps> = ({target, onDeleted}) => {
    const {t} = useTranslation();
    const emptyText = useEmptyText();
    const model = useParticipantDetail(target, onDeleted);
    const {
        activeDetail,
        detailQuery,
        primaryRegistration,
        latestCertificate,
        participantFullName,
        pending,
        actions,
        qr
    } = model;

    useShellBreadcrumbLabel(shellBreadcrumbKeys.participant(target.participantId), participantFullName);

    return (
        <div className="ati-stack ati-stack--loose">
            <PageHeader
                level={2}
                title={participantFullName
                    ? t("participant-page-title", {
                        participantFullName,
                        defaultValue: `Sudionik - ${participantFullName}`
                    })
                    : t("participant-detail", {defaultValue: "Participant detail"})}
                description={t("participant-page-subtitle", {
                    defaultValue: "Participant details, payer, accommodation and activities."
                })}
                actions={(
                    <>
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined/>}
                            disabled={!activeDetail || model.registrations.length > 0}
                            onClick={model.registrationModal.show}
                        >
                            {t("participant-register", {defaultValue: "Register"})}
                        </Button>
                        <Button
                            icon={<MailOutlined/>}
                            loading={!!primaryRegistration && pending.invitingQrRegistrationId === primaryRegistration.id}
                            disabled={!primaryRegistration || !qr.primaryState?.canSend}
                            onClick={() => primaryRegistration ? actions.sendQrInvitation(primaryRegistration) : undefined}
                        >
                            {qr.primaryState?.actionLabel ?? t("send-qr", {defaultValue: "Send QR"})}
                        </Button>
                        <Button
                            icon={<FileDoneOutlined/>}
                            loading={!!primaryRegistration && pending.generatingCertificateRegistrationId === primaryRegistration.id}
                            disabled={!model.canGeneratePrimaryCertificate}
                            onClick={() => primaryRegistration ? actions.generateCertificate(primaryRegistration) : undefined}
                        >
                            {t("generate-certificate", {defaultValue: "Generate certificate"})}
                        </Button>
                        <Button
                            icon={<DownloadOutlined/>}
                            loading={!!latestCertificate && pending.downloadingCertificateId === latestCertificate.id}
                            disabled={!primaryRegistration || !latestCertificate?.fileId}
                            onClick={() => latestCertificate ? actions.downloadCertificate(latestCertificate) : undefined}
                        >
                            {t("download-certificate", {defaultValue: "Download certificate"})}
                        </Button>
                        <DeletePopconfirm
                            itemType={t("delete-target-participant", {defaultValue: "participant"})}
                            itemName={participantFullName || target.fallbackName}
                            onConfirm={actions.deleteParticipant}
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined/>}
                                loading={pending.deletingParticipant}
                                disabled={!activeDetail}
                            >
                                {t("delete", {defaultValue: "Delete"})}
                            </Button>
                        </DeletePopconfirm>
                    </>
                )}
            />

            {detailQuery.error ? (
                <Alert
                    type="error"
                    showIcon
                    message={t("error-loading-items", {defaultValue: "Error loading items"})}
                    description={getUserFacingErrorMessage(detailQuery.error, t, buildUserFacingErrorLabels(t))}
                />
            ) : null}

            {model.isUsingFallbackDetail ? (
                <Alert
                    type="warning"
                    showIcon
                    message={t("participant-detail-partial-title", {defaultValue: "Participant details are partially available"})}
                    description={t("participant-detail-partial-description", {
                        defaultValue: "Basic details are loaded, but operational details for registrations, offers and QR status are currently unavailable for this record."
                    })}
                />
            ) : null}

            {detailQuery.loading && !activeDetail ? (
                <Skeleton active paragraph={{rows: 10}}/>
            ) : null}

            {!detailQuery.loading && !activeDetail && !detailQuery.error ? (
                <Empty description={emptyText}/>
            ) : null}

            {activeDetail ? (
                <>
                    <SystemDataSection model={model}/>
                    <RegistrationSection model={model}/>
                    <PayerSection model={model}/>
                    <OffersPaymentsSection model={model}/>
                    <ActivitiesSection model={model}/>
                    <RoomingSection model={model}/>
                    <QrInvitationSection model={model}/>
                    <CertificatesSection model={model}/>
                    <OrganizerRegistrationModal
                        eventId={activeDetail.eventId}
                        open={model.registrationModal.open}
                        participant={{
                            id: activeDetail.participantId,
                            firstName: activeDetail.firstName,
                            lastName: activeDetail.lastName,
                            email: activeDetail.email,
                            phoneNumber: activeDetail.phoneNumber,
                            company: activeDetail.company,
                            linkedInUrl: activeDetail.linkedInUrl
                        }}
                        onClose={model.registrationModal.close}
                        onRegistered={() => void model.registrationModal.onRegistered()}
                    />
                </>
            ) : null}
        </div>
    );
};
